"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CaseStudies() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tales = [{
    id: 1,
    metric: "+22%",
    metricLabel: "OTA Revenue Lift",
    badge: "OTA Strategy",
    title: "Midscale Hotel OTA Expansion",
    teaser: "Restructured extranet promotions, fixed rate parity leaks, and boosted search rank across OTAs for a 45-room boutique property.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization.webp-wvc-sizes",
    alt: "OTA optimization case study showing increased visibility and direct booking conversion",
    href: "https://hotelrevbaba.com/case-study-how-sunil-singh-increased-a-midscale-hotels-ota-revenue-by-22/"
  }, {
    id: 2,
    metric: "15–25%",
    metricLabel: "Net Revenue Lift",
    badge: "Channel Manager & PMS",
    title: "Fixing Distribution & Disparity",
    teaser: "Mapped channel manager inventory directly to the PMS, shut down unauthorized wholesale undercutting, and set automated pickup rules.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_implementation.png-wvc-sizes",
    alt: "Hotel channel manager and PMS distribution dashboard",
    to: "/CaseStudiesPage"
  }, {
    id: 3,
    metric: "15–25%",
    metricLabel: "RevPAR Improvement",
    badge: "Revenue Audit",
    title: "Plugging Margin Leaks on High-Demand Dates",
    teaser: "Audited weekend commission tiers, tightened cancellation policies, and introduced minimum stay restrictions during peak wedding season.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit.webp-wvc-sizes",
    alt: "Revenue audit case study showing RevPAR improvement",
    to: "/CaseStudiesPage"
  }];
  return <section data-section-id="11789" id="hotelcasestudies" className="relative bg-background text-foreground py-20 md:py-28 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-[1240px] px-6 md:px-10 lg:px-16">
        {/* 8/4 Split Header */}
        <div ref={introRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-14 md:mb-18 transition-all duration-[600ms] ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="h-px w-6 bg-secondary" />
              <p className="font-sans text-xs font-bold uppercase tracking-[0.2em] text-secondary">
                Client Results
              </p>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.15] tracking-tight text-foreground">
              Real Numbers from <span className="text-secondary">Independent Hotels</span>
            </h2>
            <p className="font-sans mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Practical revenue management work: fixing OTA undercutting, tuning stay restrictions, and adjusting daily room prices to protect hotel margins.
            </p>
          </div>

          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <Button asChild variant="secondary" size="lg" className="rounded-xl font-semibold w-full sm:w-auto shadow-sm">
              <Link to="/ContactPage#start-your-revenue-journey" className="inline-flex items-center justify-center gap-2">
                <span>Request Revenue Audit</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* 3-Column Case Study Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {tales.map((tale, i) => <div key={tale.id} data-index={i} className="flex">
              <Card className="h-full w-full bg-card text-card-foreground transition-all duration-300 ease-out hover:shadow-xl hover:border-secondary/40 border border-border/60 rounded-2xl overflow-hidden pt-0 flex flex-col group">
                {/* Image Section */}
                <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                  <img src={tale.image} data-wvc-srcset={tale.srcset} data-wvc-sizes={tale.sizes} alt={tale.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-primary/15 group-hover:bg-transparent transition-colors duration-500" />
                  <div className="absolute top-4 left-4">
                    <Badge variant="outline" className="bg-card/90 backdrop-blur-sm border-secondary/40 text-foreground font-semibold tracking-wider uppercase text-[10px] rounded-lg px-2.5 py-1">
                      {tale.badge}
                    </Badge>
                  </div>
                </div>

                {/* Content Section */}
                <div className="flex-1 p-6 md:p-7 flex flex-col">
                  {/* Gold Metric Callout */}
                  <div className="bg-secondary/10 border border-secondary/20 rounded-xl px-4 py-3 mb-5 flex items-baseline justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className="font-serif text-3xl font-bold text-secondary tracking-tight">
                        {tale.metric}
                      </span>
                      <span className="font-sans text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        {tale.metricLabel}
                      </span>
                    </div>
                  </div>

                  <h3 className="font-serif text-xl lg:text-2xl font-bold leading-snug text-foreground mb-3 group-hover:text-secondary transition-colors duration-300">
                    {tale.title}
                  </h3>

                  <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-6 line-clamp-3">
                    {tale.teaser}
                  </p>

                  <div className="mt-auto pt-5 border-t border-border/50">
                    {tale.href ? <Link href={tale.href} className="inline-flex items-center justify-between w-full text-foreground group-hover:text-secondary transition-colors font-sans text-sm font-semibold">
                        <span>Read Case Study</span>
                        <div className="size-8 rounded-full bg-muted flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300">
                          <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </Link> : <Link to={tale.to || "/CaseStudiesPage"} className="inline-flex items-center justify-between w-full text-foreground group-hover:text-secondary transition-colors font-sans text-sm font-semibold">
                        <span>Read Case Study</span>
                        <div className="size-8 rounded-full bg-muted flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300">
                          <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </div>
                      </Link>}
                  </div>
                </div>
              </Card>
            </div>)}
        </div>

        {/* Bottom Hub Exploration Banner */}
        <div className="mt-14 pt-8 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground text-center sm:text-left">
            Looking for more performance breakdowns, pricing benchmarks, and operational guides?
          </p>
          <div className="flex items-center gap-4 shrink-0">
            <Link to="/CaseStudiesPage" className="text-sm font-semibold text-foreground hover:text-secondary inline-flex items-center gap-1.5 transition-colors">
              <span>View all case studies</span>
              <ArrowRight className="size-4" />
            </Link>
            <span className="text-border" aria-hidden="true">&bull;</span>
            <Link to="/InsightsPage" className="text-sm font-semibold text-secondary hover:text-secondary/80 inline-flex items-center gap-1.5 transition-colors">
              <span>Read more insights</span>
              <ArrowRight className="size-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}